import { Breakpoint, styled } from "@mui/material";
import DomPurify from "dompurify";
import { marked, Tokens } from "marked";
import * as React from "react";
import FocusManager from "../core/FocusManager";

export type Justification = "Centered" | "Full" | "Left";

const firstChildSelector =
  "&>:first-child, " +
  "&>:first-child>li:first-child, " +
  "&>:first-child>li:first-child>p:first-child";

const lastSubChildSelector =
  "&>:last-child, " +
  "&>:last-child>:last-child, " +
  "&>:last-child>:last-child>:last-child, " +
  "&>:last-child>:last-child>:last-child>:last-child," +
  "&>:last-child>:last-child>:last-child>:last-child>:last-child," +
  "&>:last-child>:last-child>:last-child>:last-child>:last-child>:last-child";

interface Props {
  autoFocus?: true;
  justification: { [key in Breakpoint]: Justification };
  value: string;
}

interface MarkdownContainerProps {
  justification: { [key in Breakpoint]: Justification };
}

marked.use({
  breaks: false,
  gfm: false,
  hooks: {
    postprocess: (html: string) => {
      return DomPurify.sanitize(html, {
        ALLOWED_ATTR: ["target", "href", "title"],
        ALLOWED_TAGS: [
          "a",
          "blockquote",
          "em",
          "h1",
          "h2",
          "h3",
          "h4",
          "h5",
          "h6",
          "hr",
          "li",
          "ol",
          "p",
          "strong",
          "ul",
        ],
      });
    },
  },
  renderer: {
    code(token: Tokens.Code) {
      return `<p>${token.raw}</p>`;
    },
    codespan(token: Tokens.Codespan) {
      return token.raw;
    },
    html(token: Tokens.HTML | Tokens.Tag) {
      // No HTML tags are interpreted in the flavour of Markdown that we
      // support.
      return token.raw.replace(/</g, "&lt;").replace(/>/g, "&gt;");
    },
    image(token: Tokens.Image) {
      return token.raw;
    },
    link(token: Tokens.Link) {
      // Helper function "borrowed" from marked.js
      // https://github.com/markedjs/marked/blob/v14.1.0/src/helpers.ts
      function cleanUrl(href: string): string | null {
        try {
          href = encodeURI(href).replace(/%25/g, "%");
        } catch {
          return null;
        }
        return href;
      }

      // Disable auto-links
      // https://www.markdownguide.org/basic-syntax/#urls-and-email-addresses
      if (token.raw.startsWith("<") || token.raw.endsWith(">")) {
        return token.raw.replace(/</g, "&lt;").replace(/>/g, "&gt;");
      }

      // Implementation based off the default Link renderer
      // https://github.com/markedjs/marked/blob/v14.1.0/src/Renderer.ts
      const text = this.parser.parseInline(token.tokens);
      const cleanHref = cleanUrl(token.href);
      if (cleanHref === null) {
        return text;
      }

      const title = token.title ? ` title="${token.title}"` : "";
      return `<a href="${cleanHref}" target="_blank"${title}>${text}</a>`;
    },
  },
});

const MarkdownContainer = styled("div")<MarkdownContainerProps>(
  ({ justification, theme }) => {
    const textAlignByJustification = {
      Centered: "center",
      Full: "justify",
      Left: "start",
    };

    const listAlignStyle = theme.breakpoints.keys.reduce(
      (result, breakpoint) => {
        const listJustification =
          justification[breakpoint] === "Centered"
            ? "Left"
            : justification[breakpoint];

        result[theme.breakpoints.only(breakpoint)] = {
          textAlign: textAlignByJustification[listJustification],
        };
        return result;
      },
      {} as { [key: string]: { textAlign: string } }
    );

    const textAlignStyle = theme.breakpoints.keys.reduce(
      (result, breakpoint) => {
        result[theme.breakpoints.only(breakpoint)] = {
          textAlign: textAlignByJustification[justification[breakpoint]],
        };
        return result;
      },
      {} as { [key: string]: { textAlign: string } }
    );

    return {
      fontFamily: "Roboto, sans-serif",
      fontWeight: 400,
      color: theme.palette.text.primary,
      "&:focus": {
        outline: "none",
      },
      [firstChildSelector]: {
        marginTop: 0,
      },
      [lastSubChildSelector]: {
        marginBottom: 0,
      },
      "& a": {
        color: theme.palette.info.main,
        fontWeight: 700,
      },
      "& blockquote": {
        backgroundColor: theme.palette.grey[100],
        borderLeft: `8px solid ${theme.palette.grey[800]}`,
        boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.25)",
        fontWeight: 300,
        margin: "24px 0",
        padding: "12px 24px",
        [firstChildSelector]: {
          marginTop: 0,
        },
        [lastSubChildSelector]: {
          marginBottom: 0,
        },
      },
      "& h1,h2,h3,h4,h5,h6": {
        fontWeight: 700,
        margin: "40px 0 24px 0",
        ...textAlignStyle,
      },
      "& h1": {
        fontSize: "34px",
        lineHeight: "44px",
        [theme.breakpoints.up("sm")]: {
          fontSize: "45px",
          lineHeight: "60px",
          marginTop: "60px",
        },
        [theme.breakpoints.up("lg")]: {
          fontSize: "56px",
          lineHeight: "72px",
          marginTop: "80px",
        },
      },
      "& h2": {
        fontSize: "24px",
        lineHeight: "32px",
        [theme.breakpoints.up("sm")]: {
          fontSize: "34px",
          lineHeight: "44px",
        },
        [theme.breakpoints.up("lg")]: {
          fontSize: "45px",
          lineHeight: "60px",
          marginTop: "60px",
        },
      },
      "& h3": {
        fontSize: "21px",
        lineHeight: "28px",
        [theme.breakpoints.up("sm")]: {
          fontSize: "24px",
          lineHeight: "32px",
        },
        [theme.breakpoints.up("lg")]: {
          fontSize: "34px",
          lineHeight: "44px",
        },
      },
      "& h4": {
        fontSize: "16px",
        lineHeight: "20px",
        [theme.breakpoints.up("sm")]: {
          fontSize: "21px",
          lineHeight: "28px",
        },
        [theme.breakpoints.up("lg")]: {
          fontSize: "24px",
          lineHeight: "32px",
        },
      },
      "& h5": {
        fontSize: "16px",
        lineHeight: "20px",
        [theme.breakpoints.up("lg")]: {
          fontSize: "21px",
          lineHeight: "28px",
        },
      },
      "& h6": {
        fontSize: "16px",
        lineHeight: "20px",
      },
      "& hr": {
        borderColor: theme.palette.divider,
        borderStyle: "solid",
        borderWidth: 0,
        borderBottomWidth: 1,
        margin: "24px 0",
      },
      "& li": {
        margin: "24px 0",
      },
      "& li li": {
        margin: "8px 0",
      },
      "& li>p": {
        margin: "8px 0",
        ...listAlignStyle,
      },
      "& ol": {
        paddingInlineStart: "40px",
        ...listAlignStyle,
      },
      "& p": {
        fontSize: "16px",
        lineHeight: "20px",
        margin: "24px 0",
        ...textAlignStyle,
      },
      "& strong": {
        fontWeight: 700,
      },
      "& ul": {
        paddingInlineStart: "40px",
        ...listAlignStyle,
      },
    };
  }
);

export default function Markdown(props: Props): JSX.Element {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const cleanHtml = React.useMemo(
    () => marked.parse(props.value) as string,
    [props.value]
  );

  React.useEffect(() => {
    if (props.autoFocus && containerRef.current) {
      FocusManager.grabFocus(containerRef.current);
    }
  }, [props.autoFocus, containerRef]);

  return (
    <MarkdownContainer
      dangerouslySetInnerHTML={{ __html: cleanHtml }}
      justification={props.justification}
      ref={containerRef}
      tabIndex={props.autoFocus ? -1 : undefined}
    />
  );
}
